import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const SUB_TONES = {
	default: '#8c8c8c',
	success: '#267003',
	danger: '#B11500',
	warning: '#8a5000',
};

const BORDER_TONES = {
	default: '#e8e8e8',
	success: '#8fd0a8',
	danger: '#f0a9a2',
	warning: '#f0d9a8',
};

const Card = styled.div`
	background: #fff;
	border: 1px solid ${(props) => BORDER_TONES[props.borderTone]};
	border-radius: 8px;
	padding: 20px;
	height: 100%;
	display: flex;
	flex-direction: column;
	gap: 10px;
`;

const CardHead = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`;

const Label = styled.span`
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.4px;
	text-transform: uppercase;
	color: #8c8c8c;
`;

const IconSlot = styled.span`
	color: #8c8c8c;
	font-size: 14px;
	line-height: 1;
	flex-shrink: 0;
`;

const Value = styled.div`
	font-size: 26px;
	font-weight: 600;
	line-height: 1.2;
	color: ${(props) => (props.tone === 'default' ? '#18405d' : SUB_TONES[props.tone])};
	font-variant-numeric: tabular-nums;
`;

const SubText = styled.div`
	font-size: 12px;
	line-height: 1.45;
	color: ${(props) => SUB_TONES[props.tone]};
`;

const Skeleton = styled.div`
	height: ${(props) => props.h}px;
	width: ${(props) => props.w};
	border-radius: 4px;
	background: linear-gradient(90deg, #f2f2f2 25%, #e9e9e9 37%, #f2f2f2 63%);
	background-size: 400% 100%;
	animation: statCardShimmer 1.4s ease infinite;

	@keyframes statCardShimmer {
		0% {
			background-position: 100% 50%;
		}
		100% {
			background-position: 0 50%;
		}
	}
`;

/**
 * KPI tile used by the warehousing dashboards.
 *
 * `tone` colours the value, `subTone` colours the sub-line independently — a neutral
 * value with a red sub-line is a common combination (e.g. an approximate cost figure).
 */
export default function StatCard({
	label,
	value,
	sub,
	icon,
	tone,
	subTone,
	borderTone,
	badge,
	loading,
}) {
	return (
		<Card borderTone={borderTone}>
			<CardHead>
				<Label>{label}</Label>
				{badge || (icon && <IconSlot>{icon}</IconSlot>)}
			</CardHead>

			{loading ? (
				<>
					<Skeleton h={30} w="70%" />
					<Skeleton h={12} w="50%" />
				</>
			) : (
				<>
					<Value tone={tone}>{value}</Value>
					{sub && <SubText tone={subTone}>{sub}</SubText>}
				</>
			)}
		</Card>
	);
}

StatCard.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.node,
	sub: PropTypes.node,
	icon: PropTypes.node,
	tone: PropTypes.oneOf(['default', 'success', 'danger', 'warning']),
	subTone: PropTypes.oneOf(['default', 'success', 'danger', 'warning']),
	borderTone: PropTypes.oneOf(['default', 'success', 'danger', 'warning']),
	badge: PropTypes.node,
	loading: PropTypes.bool,
};

StatCard.defaultProps = {
	value: null,
	sub: null,
	icon: null,
	tone: 'default',
	subTone: 'default',
	borderTone: 'default',
	badge: null,
	loading: false,
};
